import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { QrCode, Edit2, Mail, Trash2 } from "lucide-react";

interface StaffCardProps {
  id: string;
  name: string;
  isActive: boolean;
  reviewsSent: number;
  responseRate: number;
  onToggleActive: (id: string, isActive: boolean) => void;
  onShowQR: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete?: (id: string) => void;
}

export function StaffCard({
  id,
  name,
  isActive,
  reviewsSent,
  responseRate,
  onToggleActive,
  onShowQR,
  onEdit,
  onDelete,
}: StaffCardProps) {
  const initials = name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <Card data-testid={`staff-card-${id}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <Avatar className="h-12 w-12">
            <AvatarFallback className="bg-primary/10 text-primary font-medium">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-medium truncate">{name}</h3>
              <Switch
                checked={isActive}
                onCheckedChange={(checked) => onToggleActive(id, checked)}
                data-testid={`switch-staff-active-${id}`}
              />
            </div>
            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Mail className="h-3.5 w-3.5" />
                <span>{reviewsSent} sent</span>
              </div>
              <div>
                <span>{responseRate}% response</span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => onShowQR(id)}
            data-testid={`button-show-qr-${id}`}
            aria-label={`Show QR code for ${name}`}
          >
            <QrCode className="h-4 w-4 mr-2" />
            QR Code
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onEdit(id)}
            data-testid={`button-edit-staff-${id}`}
            aria-label={`Edit ${name}`}
          >
            <Edit2 className="h-4 w-4" />
          </Button>
          {onDelete && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDelete(id)}
              data-testid={`button-delete-staff-${id}`}
              aria-label={`Delete ${name}`}
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
